import { enumMembers, type ApiSchema } from "./apiSchema";
import { jsonBody, requestJson } from "./http/httpClient";
import { readArray, readBoolean, readNullableString, readObject, readOneOf, readString, type JsonObject } from "./http/jsonDecoding";
import { decodePerson, type HistoryPerson } from "./people";

/**
 * Deleting a project and the deleted projects (F121, accounts.md): a project's own
 * administrators and administrators delete it from its settings' danger zone, by typing its
 * name once more; administrators restore it or, once its restore period is over, purge it.
 */

export type ProjectState = ApiSchema<"ProjectState">;

export async function renameProject(projectId: string, name: string): Promise<void> {
  const body: ApiSchema<"ProjectRenameBody"> = { name };
  await requestJson({
    method: "PUT",
    path: `/projects/${encodeURIComponent(projectId)}/name`,
    body: jsonBody(body),
    decodeResponse: (response) => readObject(response, "renamed project"),
  });
}

const REMOVED_STATES = enumMembers<Exclude<ProjectState, "active">>({ deleted: true, purging: true, purged: true });

export interface DeletedProject {
  readonly projectId: string;
  readonly name: string;
  /** `purging`: a purge was cut off; purging again finishes it. */
  readonly state: Exclude<ProjectState, "active">;
  readonly deletedBy: HistoryPerson;
  /** ISO 8601. */
  readonly deletedAt: string;
  /** ISO 8601: from then on it may be purged; until it is, it can be restored. */
  readonly purgeableFrom: string;
  readonly purgedBy: HistoryPerson | null;
  readonly purgedAt: string | null;
  /** False while some module can't purge a project's data yet: the project stays restorable. */
  readonly purgeAvailable: boolean;
}

/** Deletes the project; `typedName` is its name as the person typed it once more. */
export function deleteProject(projectId: string, typedName: string): Promise<DeletedProject> {
  return requestJson({
    method: "POST",
    path: `/projects/${encodeURIComponent(projectId)}/deletion`,
    body: jsonBody(typedNameBody(typedName)),
    decodeResponse: (body) => decodeDeletedProject(readObject(body, "deleted project")),
  });
}

export function listDeletedProjects(signal?: AbortSignal): Promise<readonly DeletedProject[]> {
  return requestJson({
    method: "GET",
    path: DELETED_PROJECTS_PATH,
    signal,
    decodeResponse: (body) => readArray(body, "deleted projects", (item) => decodeDeletedProject(readObject(item, "deleted project"))),
  });
}

export async function restoreProject(projectId: string): Promise<void> {
  await requestJson({
    method: "POST",
    path: `${DELETED_PROJECTS_PATH}/${encodeURIComponent(projectId)}/restore`,
    decodeResponse: (body) => readObject(body, "restored project"),
  });
}

export function purgeProject(projectId: string, typedName: string): Promise<DeletedProject> {
  return requestJson({
    method: "POST",
    path: `${DELETED_PROJECTS_PATH}/${encodeURIComponent(projectId)}/purge`,
    body: jsonBody(typedNameBody(typedName)),
    decodeResponse: (body) => decodeDeletedProject(readObject(body, "purged project")),
  });
}

const DELETED_PROJECTS_PATH = "/deleted-projects";

function typedNameBody(typedName: string): ApiSchema<"TypedProjectNameBody"> {
  return { typed_name: typedName };
}

function decodeDeletedProject(project: JsonObject): DeletedProject {
  const purgedBy = project["purged_by"];
  return {
    projectId: readString(project, "id"),
    name: readString(project, "name"),
    state: readOneOf(project, "state", REMOVED_STATES),
    deletedBy: decodePerson(readObject(project["deleted_by"], "deleted by")),
    deletedAt: readString(project, "deleted_at"),
    purgeableFrom: readString(project, "purgeable_from"),
    purgedBy: purgedBy === null || purgedBy === undefined ? null : decodePerson(readObject(purgedBy, "purged by")),
    purgedAt: readNullableString(project, "purged_at"),
    purgeAvailable: readBoolean(project, "purge_available"),
  };
}
